// Phase 4 (api-phase4.md "AI watches", prototype pcard .watch and drawerHTML 'park'): AI watches in the console.
//   WatchLine    the violet line on parked cards and the dock: "AI watching Cam 01, 02 for police · 06:12" (countdown
//                to expiry), "AI checking a possible hit", or the hit with its frame.
//   WatchList    in the Agent tab (design/README.md 2026-10-16: no watch row in the workspace): every watch with its
//                status, countdown, hit frame and Cancel
//                (DELETE /api/watches/:id); Add watch on an event I hold or have parked (POST /api/events/:id/watches);
//                and, in the devstack, Simulate detection for admins.
//   CameraChips, WatchFields  shared with the Park drawer.
//   SimDetect    dev only: what a camera "sees" (PUT /api/dev/sim/detections/:cameraId), so a watch can hit.

import { useState } from 'react';
import { api, errorText, SIM_LABELS, type EventCamera, type SimLabel, type Watch, type WatchCondition, type WatchInput } from '../api.ts';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon } from '../components/Icon.tsx';
import { InlineError } from '../components/ui.tsx';
import { useNow } from '../hooks.ts';
import { useSession } from '../session.tsx';
import { fmtTime } from '../time.ts';
import { camList, CONDITION_OPTIONS, pct, watchLive, watchWhat } from './ai.ts';
import type { Ctl } from './ctl.ts';
import { camShort, dur, human, ms } from './util.ts';

/* ---------- compact line (parked cards, dock, park bar) ---------- */

export function Countdown({ to, prefix = '' }: { to: string; prefix?: string }) {
  const now = useNow(1000);
  const t = ms(to);
  if (t === null) return null;
  return <span className={`mono${t - now < 60_000 ? ' late' : ''}`}>{t > now ? `${prefix}${dur(t - now)}` : 'ending'}</span>;
}

/** The newest hit, if the watches ended in one. */
function lastHit(ws: Watch[]): Watch | undefined {
  return ws
    .filter((w) => w.status === 'hit')
    .sort((a, b) => (ms(b.hitAt) ?? 0) - (ms(a.hitAt) ?? 0))[0];
}

export function WatchLine({ ws, frame = true }: { ws: Watch[] | undefined; frame?: boolean }) {
  if (!ws?.length) return null;
  const live = ws.filter(watchLive);
  const confirming = live.find((w) => w.status === 'confirming');
  if (confirming) {
    const cam = (confirming.hitCameraId ? confirming.cameraNames[confirming.cameraIds.indexOf(confirming.hitCameraId)] : null) ?? confirming.cameraNames[0];
    return (
      <div className="watch confirming" title="The agent is looking at the frame before it raises the event">
        <Icon name="eye" className="eyepulse" />
        <span>
          AI checking a possible {watchWhat(confirming)} on {camShort(cam)}
        </span>
      </div>
    );
  }
  if (live.length) {
    const soonest = live.map((w) => w.expiresAt).sort()[0] ?? live[0]!.expiresAt;
    const cams = camList([...new Set(live.flatMap((w) => w.cameraNames))]);
    const what = [...new Set(live.map(watchWhat))].join(', ');
    return (
      <div className="watch" title={live.map((w) => `${watchWhat(w)} on ${w.cameraNames.join(', ')} until ${new Date(w.expiresAt).toLocaleTimeString()}${w.byAgent ? ' (set by the AI)' : ''}`).join('\n')}>
        <Icon name="eye" className="eyepulse" />
        <span>
          AI watching {cams} for {what} · <Countdown to={soonest} />
        </span>
      </div>
    );
  }
  const hit = lastHit(ws);
  if (hit) {
    const cam = hit.cameraNames[hit.cameraIds.indexOf(hit.hitCameraId ?? '')] ?? hit.hitCameraId ?? '';
    return (
      <div className="watch hit">
        {frame && hit.frameUrl ? (
          <span className="wthumb">
            <AuthImg url={hit.frameUrl} alt={`Frame: ${watchWhat(hit)} on ${cam}`} lazy fallback="" />
          </span>
        ) : (
          <Icon name="eye" />
        )}
        <span>
          AI watch saw {watchWhat(hit)} on {camShort(cam)}
          {hit.hitConfidence !== null ? ` (${pct(hit.hitConfidence)})` : ''}
        </span>
      </div>
    );
  }
  return null;
}

/* ---------- shared pickers ---------- */

/** The event's cameras, alarm cameras first (then by distance), as toggle chips. */
export function CameraChips({ cams, alarmCamIds, value, onChange, label }: { cams: EventCamera[]; alarmCamIds: ReadonlySet<string>; value: string[]; onChange: (v: string[]) => void; label: string }) {
  const sorted = cams.slice().sort((a, b) => Number(alarmCamIds.has(b.id)) - Number(alarmCamIds.has(a.id)) || a.distanceRank - b.distanceRank);
  return (
    <div className="chips" role="group" aria-label={label}>
      {sorted.map((c) => {
        const on = value.includes(c.id);
        const off = c.health?.status === 'offline';
        return (
          <button
            key={c.id}
            type="button"
            className={`chip aichip${on ? ' on' : ''}`}
            aria-pressed={on}
            title={`${c.name}${alarmCamIds.has(c.id) ? ' · alarm camera' : ''}${off ? ' · offline' : ''}`}
            onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}
          >
            {camShort(c.name)}
            <span className="dim">{c.name.split(' · ')[1] ? ` · ${c.name.split(' · ')[1]}` : ''}</span>
            {alarmCamIds.has(c.id) && <span className="alarm-t"> ·A</span>}
            {off && <span className="warn-t"> ·off</span>}
          </button>
        );
      })}
    </div>
  );
}

export const WATCH_MINUTES = [5, 10, 15, 30, 60, 120, 240] as const;

/** Minutes, minimum dwell, and the condition's extra field (custom description, gate state). */
export function WatchFields({
  conditions,
  minutes,
  setMinutes,
  untilLabel,
  dwell,
  setDwell,
  description,
  setDescription,
  gate,
  setGate,
  tried,
}: {
  conditions: string[];
  minutes: number | null;
  setMinutes: (m: number | null) => void;
  /** What "no minutes" means: "Until the fallback timer (15 min)". */
  untilLabel: string;
  dwell: number;
  setDwell: (n: number) => void;
  description: string;
  setDescription: (s: string) => void;
  gate: 'open' | 'closed';
  setGate: (g: 'open' | 'closed') => void;
  tried: boolean;
}) {
  return (
    <>
      {conditions.includes('custom') && (
        <label className="field">
          <span className="lbl">Custom · what should it look for</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. a white pickup stopping at the gate" maxLength={500} aria-invalid={tried && !description.trim()} />
          {tried && !description.trim() && <span className="inline-err">Describe what the custom watch looks for.</span>}
        </label>
      )}
      {conditions.includes('gate_state') && (
        <div className="field">
          <span className="lbl">Gate · raise when it is</span>
          <div className="seg" role="radiogroup" aria-label="Gate state">
            {(['open', 'closed'] as const).map((g) => (
              <button key={g} type="button" role="radio" aria-checked={gate === g} className={gate === g ? 'on' : ''} onClick={() => setGate(g)}>
                {g === 'open' ? 'Opened' : 'Closed'}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="row2">
        <label className="field">
          <span className="lbl">Watch for</span>
          <select value={minutes ?? ''} onChange={(e) => setMinutes(e.target.value ? Number(e.target.value) : null)}>
            <option value="">{untilLabel}</option>
            {WATCH_MINUTES.map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} min` : `${m / 60} h`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Minimum dwell</span>
          <select value={dwell} onChange={(e) => setDwell(Number(e.target.value))}>
            {[0, 3, 5, 10, 20, 30, 60].map((s) => (
              <option key={s} value={s}>
                {s === 0 ? 'Raise at once' : `In view ${s} s first`}
              </option>
            ))}
          </select>
        </label>
      </div>
    </>
  );
}

/** One WatchInput per condition, all on the same cameras. */
export function watchInputs(o: { conditions: string[]; cameraIds: string[]; minutes: number | null; dwell: number; description: string; gate: 'open' | 'closed' }): WatchInput[] {
  return o.conditions.map((cond) => ({
    cameraIds: o.cameraIds,
    condition: cond as WatchCondition,
    ...(o.minutes ? { minutes: o.minutes } : {}),
    ...(o.dwell ? { minDwellS: o.dwell } : {}),
    ...(cond === 'custom' ? { description: o.description.trim() } : {}),
    ...(cond === 'gate_state' ? { state: o.gate } : {}),
  }));
}

/* ---------- the event's watch panel ---------- */

const STATUS_TXT: Record<string, string> = {
  active: 'watching',
  confirming: 'AI checking a hit',
  hit: 'hit',
  expired: 'ended, no hit',
  cancelled: 'ended',
};

const ENDED_TXT: Record<string, string> = {
  raised: 'the event was raised',
  resumed: 'the event was resumed',
  time: 'time ran out',
  cancelled: 'cancelled',
};

function WatchRow({ c, w, canEdit }: { c: Ctl; w: Watch; canEdit: boolean }) {
  const [busy, setBusy] = useState(false);
  const live = watchLive(w);
  const tz = c.ev.siteTimeZone;
  const hitCam = w.hitCameraId ? (w.cameraNames[w.cameraIds.indexOf(w.hitCameraId)] ?? w.hitCameraId) : null;
  const cancel = async () => {
    setBusy(true);
    try {
      await api.cancelWatch(w.id);
      c.reloadWatches();
    } catch (e) {
      c.toast({ tone: 'bad', title: 'Could not cancel the watch', body: errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`wrow s-${w.status}`}>
      <Icon name="eye" className={live ? 'eyepulse' : undefined} />
      <div className="wmain">
        <div>
          <b>{watchWhat(w)}</b> on {camList(w.cameraNames)}
          {w.byAgent && <span className="tagx ai">set by AI</span>}
          {w.detector === 'llm_confirm' && <span className="tagx" title="The agent checks each hit on the frame before it raises (a hit it doesn't answer in 20 s raises anyway)">AI confirms</span>}
        </div>
        <div className="note">
          <span className={`wst s-${w.status}`}>{STATUS_TXT[w.status] ?? human(w.status)}</span>
          {live && (
            <>
              {' · '}
              <Countdown to={w.expiresAt} prefix="ends in " />
            </>
          )}
          {w.status === 'hit' && w.hitAt && ` · ${fmtTime(w.hitAt, tz)} on ${camShort(hitCam)}${w.hitConfidence !== null ? ` (${pct(w.hitConfidence)})` : ''}`}
          {!live && w.status !== 'hit' && w.endedReason && ` · ${ENDED_TXT[w.endedReason] ?? human(w.endedReason).toLowerCase()}`}
          {w.rejectedCount > 0 && ` · ${w.rejectedCount} possible hit${w.rejectedCount === 1 ? '' : 's'} the AI rejected`}
        </div>
      </div>
      {w.frameUrl && (
        <span className="wframe" title="The frame that tripped the watch">
          <AuthImg url={w.frameUrl} alt={`Frame: ${watchWhat(w)} on ${hitCam ?? 'camera'}`} lazy fallback="" />
        </span>
      )}
      {live && canEdit && (
        <button type="button" className="btn sm" onClick={() => void cancel()} disabled={busy}>
          {busy ? '…' : 'Cancel'}
        </button>
      )}
    </div>
  );
}

function AddWatch({ c, onDone }: { c: Ctl; onDone: () => void }) {
  const ev = c.ev;
  const alarmCams = new Set(ev.alarms.map((a) => a.cameraId).filter((x): x is string => !!x));
  const [cond, setCond] = useState<string>(ev.actions.some((a) => a.type === 'dispatch' && a.status === 'succeeded') ? 'police_arrival' : 'subject_return');
  const [cams, setCams] = useState<string[]>([...alarmCams].filter((id) => ev.cameras.find((x) => x.id === id)?.health?.status !== 'offline').slice(0, 4));
  const [minutes, setMinutes] = useState<number | null>(null);
  const [dwell, setDwell] = useState(0);
  const [description, setDescription] = useState('');
  const [gate, setGate] = useState<'open' | 'closed'>('open');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const parked = ev.state === 'parked';
  const fbLeft = parked ? Math.max(1, Math.ceil(((ms(ev.park?.fallbackAt) ?? Date.now()) - Date.now()) / 60_000)) : null;
  const valid = cams.length > 0 && (cond !== 'custom' || !!description.trim());
  const submit = async () => {
    setTried(true);
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      const [body] = watchInputs({ conditions: [cond], cameraIds: cams, minutes, dwell, description, gate });
      if (!body) return;
      // The new watch shows in the list.
      await api.addWatch(ev.id, body);
      c.reloadWatches();
      onDone();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="addwatch">
      <div className="chips" role="radiogroup" aria-label="Watch for">
        {CONDITION_OPTIONS.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={cond === o.value} className={`chip aichip${cond === o.value ? ' on' : ''}`} onClick={() => setCond(o.value)} title={o.sub}>
            {o.label}
          </button>
        ))}
      </div>
      <CameraChips cams={ev.cameras} alarmCamIds={alarmCams} value={cams} onChange={setCams} label="Cameras to watch" />
      {tried && !cams.length && <span className="inline-err">Pick at least one camera.</span>}
      <WatchFields
        conditions={[cond]}
        minutes={minutes}
        setMinutes={setMinutes}
        untilLabel={fbLeft !== null ? `Until the fallback timer (${fbLeft} min)` : 'Default (60 min)'}
        dwell={dwell}
        setDwell={setDwell}
        description={description}
        setDescription={setDescription}
        gate={gate}
        setGate={setGate}
        tried={tried}
      />
      <div className="row-wrap">
        <button type="button" className="btn aib" onClick={() => void submit()} disabled={busy}>
          <Icon name="eye" />
          {busy ? 'Setting…' : 'Set watch'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
        <InlineError error={err} />
      </div>
    </div>
  );
}

/** Who may set and cancel watches here: the owner or a supervisor, while the event is active or parked. */
export function canEditWatches(c: Ctl): boolean {
  return (c.ev.state === 'active' || c.ev.state === 'parked') && (c.isSup || c.ev.owner?.id === c.meId);
}

export function WatchList({ c }: { c: Ctl }) {
  const [adding, setAdding] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const [sim, setSim] = useState(false);
  const s = useSession();
  const ws = c.watches ?? [];
  const canEdit = canEditWatches(c);
  const devSim = s.config.authMode === 'dev' && s.isAdmin;
  const live = ws.filter(watchLive);
  const ended = ws.filter((w) => !watchLive(w)).sort((a, b) => (ms(b.hitAt ?? b.expiresAt) ?? 0) - (ms(a.hitAt ?? a.expiresAt) ?? 0));
  const recentHit = ended.find((w) => w.status === 'hit');
  if (!ws.length && !canEdit) return null;
  const shownEnded = showEnded ? ended : recentHit ? [recentHit] : [];
  return (
    <div className={`sec watchpanel${live.length ? ' on' : ''}`} aria-label="AI watches">
      <div className="wphead">
        <Icon name="eye" className={live.length ? 'eyepulse' : undefined} />
        <span className="lbl ai-t">AI watches</span>
        <span className="note">
          {live.length ? `${live.length} watching` : ws.length ? 'none watching now' : 'none'}
          {ended.length > 0 && (
            <>
              {' · '}
              <button type="button" className="linkish" onClick={() => setShowEnded((v) => !v)}>
                {showEnded ? 'hide ended' : `${ended.length} ended`}
              </button>
            </>
          )}
        </span>
        <span className="sp" />
        {devSim && (live.length > 0 || sim) && (
          <button type="button" className={`btn sm${sim ? ' on' : ''}`} onClick={() => setSim((v) => !v)} aria-pressed={sim} title="Devstack only: set what a camera's simulated detector sees">
            <Icon name="bolt" />
            Simulate detection
          </button>
        )}
        {canEdit && !adding && (
          <button type="button" className="btn sm aib" onClick={() => setAdding(true)}>
            <Icon name="plus" />
            Add watch
          </button>
        )}
      </div>
      {live.map((w) => (
        <WatchRow key={w.id} c={c} w={w} canEdit={canEdit} />
      ))}
      {shownEnded.map((w) => (
        <WatchRow key={w.id} c={c} w={w} canEdit={false} />
      ))}
      {adding && <AddWatch c={c} onDone={() => setAdding(false)} />}
      {devSim && sim && <SimDetect cams={c.railCams} preferIds={[...new Set(live.flatMap((w) => w.cameraIds))]} />}
    </div>
  );
}

/* ---------- dev: simulate a detection ---------- */

/** Devstack only (admin, authMode dev): set what the simulated detector "sees" on a camera, so a watch can hit. */
export function SimDetect({ cams, preferIds }: { cams: EventCamera[]; preferIds: string[] }) {
  const [camId, setCamId] = useState(preferIds[0] ?? cams[0]?.id ?? '');
  const [labels, setLabels] = useState<SimLabel[]>(['police_vehicle']);
  const [conf, setConf] = useState(0.85);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [last, setLast] = useState<string | null>(null);
  const send = async (clear: boolean) => {
    if (!camId) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.simDetections(camId, clear ? [] : labels.map((label) => ({ label, confidence: conf })), clear ? undefined : 120);
      const name = camShort(cams.find((x) => x.id === camId)?.name ?? camId);
      setLast(r.detections.length ? `${name} sees ${r.detections.map((d) => `${d.label.replace(/_/g, ' ')} ${pct(d.confidence)}`).join(', ')} for 2 min` : `${name} sees nothing`);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="simdet" role="group" aria-label="Simulate a detection (devstack)">
      <span className="lbl">
        <Icon name="bolt" /> Dev · simulate a detection
      </span>
      <div className="row-wrap">
        <select value={camId} onChange={(e) => setCamId(e.target.value)} aria-label="Camera" className="compact">
          {cams.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
              {preferIds.includes(x.id) ? ' · watched' : ''}
            </option>
          ))}
        </select>
        <label className="row-wrap nowrap small">
          Confidence
          <input type="range" min={0.3} max={0.99} step={0.01} value={conf} onChange={(e) => setConf(Number(e.target.value))} aria-label="Confidence" />
          <span className="mono">{pct(conf)}</span>
        </label>
      </div>
      <div className="chips" role="group" aria-label="What the camera sees">
        {SIM_LABELS.map((l) => {
          const on = labels.includes(l);
          return (
            <button key={l} type="button" className={`chip${on ? ' hit' : ''}`} aria-pressed={on} onClick={() => setLabels((v) => (on ? v.filter((x) => x !== l) : [...v, l]))}>
              {l.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
      <div className="row-wrap">
        <button type="button" className="btn sm pri" onClick={() => void send(false)} disabled={busy || !labels.length || !camId}>
          Make it see this
        </button>
        <button type="button" className="btn sm" onClick={() => void send(true)} disabled={busy || !camId}>
          Clear
        </button>
        {last && <span className="note">{last}. The vision worker checks every 2 s.</span>}
        <InlineError error={err} />
      </div>
    </div>
  );
}
